import { Button, Form, Input, InputNumber, Select, Table } from "antd";
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/lib/table';
import React from "react";
import { DatePicker as DatePickerJalali } from "antd-jalali";
import { IDamageCase } from "../../baseInfo/damageCase/interfaces";
import { IInsurer } from "../../baseInfo/insurer/interfaces";
import { BaseComponent, IBaseProps, IBaseStates } from "../../components/infrastructure/baseComponent";
import { Core } from "../../components/infrastructure/core";
import { IFilter } from "../../components/infrastructure/filtering";
import { enmEntityState, ICallOptions, IFetchingInfo, IOrdering, IResult } from "../../components/infrastructure/models";
import { IDamageCaseVoucher } from "../interfaces";
import CustomSpinner from "../../components/infrastructure/customSpinner";

type LayoutType = Parameters<typeof Form>[0]['layout'];

const { Option } = Select;

interface IIssueVoucherStates extends IBaseStates {
    insurers: IInsurer[];
    data: IDamageCase[];
    totalResultCount: number;
    totalAmount: number;
    selectedRows: IDamageCase[];
}

export interface IIssueVoucherProps extends IBaseProps {
    params: () => {
        filter?: IFilter;
    }
}

interface IIssueVoucherFormProps {
    loading: boolean;
    onClose?: (sender: any) => void;
    onSubmit?: (values: any) => Promise<void>;
    insurers: IInsurer[];
    totalAmount: number;
}

const IssueVoucherForm = (props: IIssueVoucherFormProps) => {
    const formItemLayout = {
        labelCol: { xxl: 4, xl: 3, lg: 3, md: 3, sm: 6, xs: 6 },
        wrapperCol: { xxl: 8, xl: 6, lg: 6, md: 6, sm: 10, xs: 10 },
    };

    const [formLayout] = React.useState<LayoutType>('horizontal');
    const [form] = Form.useForm();

    const insurerOptions = props.insurers.map(i => <Option key={i.Id} value={i.Id}>{i.Name}</Option>)

    React.useEffect(() => {
        form.setFields([{ name: "TotalAmount", value: props.totalAmount }]);
    }, [props, form]);

    return (
        <>
            <Form
                className="box"
                style={{ padding: 20 }}
                form={form}
                {...formItemLayout}
                layout={formLayout}
                onFinish={async (values) => {
                    props.onSubmit && await props.onSubmit(values);
                }}
                onReset={() => {
                    props.onClose && props.onClose(this);
                }}
            >
                <Form.Item
                    label="بیمه گر"
                    name="InsurerId"
                    key={'InsurerId'}
                    initialValue={1}
                >
                    <Select >
                        {insurerOptions}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="تاریخ چک"
                    name="ChequeDate"
                    key={'ChequeDate'}
                >
                    <DatePickerJalali
                        format="YYYY/MM/DD"
                        inputReadOnly={true}
                        style={{ width: '100%', display: 'block' }}
                    />
                </Form.Item>

                <Form.Item
                    label="شماره چک"
                    name="ChequeNo"
                    key={'ChequeNo'}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label="مبلغ کل"
                    name="TotalAmount"
                    key={'TotalAmount'}
                >
                    <InputNumber disabled
                        style={{ width: "100%" }}
                        formatter={value => {
                            return `ريال ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }}
                    />
                </Form.Item>

                <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ marginTop: 20, borderTop: '1px solid', borderColor: '#e5e5e5' }}>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={props.loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 20 }}>
                        صدور
                    </Button>

                    <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={props.loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 20, marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>
                </Form.Item>
            </Form>
        </>
    );
}

export class IssueVoucher extends BaseComponent<IIssueVoucherProps, IIssueVoucherStates> {
    constructor(props: IIssueVoucherProps) {
        super(props)
        this.state = {
            isAuthorized: true,
            loading: true,
            insurers: [],
            data: [],
            totalResultCount: 0,
            totalAmount: 0,
            selectedRows: [],
            windowSize: window.innerWidth <= 765 ? 1 : 2
        }
    }
    componentDidMount() {
        super.componentDidMount();
        Promise.all([this.fetchData(), this.fetchInsurers()]);
    }
    fetchInsurers = async () => {
        let fetchingInfo: IFetchingInfo = {
            Orderings: [] as IOrdering[]
        };

        let fetchOpt: ICallOptions = {
            url: "insurer/get",
            data: { fetchingInfo },
            onSuccess: (result: any) => {
                this.setState({ insurers: result.Data });
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }
    fetchData = async () => {

        let fetchingInfo: IFetchingInfo = {
            Orderings: [] as IOrdering[],
            Filter: this.props.params()?.filter
        };

        let fetchOpt: ICallOptions = {
            url: "damageCaseVoucherPaymentDraft/get",
            data: { fetchingInfo },
            onSuccess: (result: any) => {
                this.setState({ data: result.Data, totalResultCount: result.TotalResultCount });
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }
    onSubmit = async (values: any) => {
        if (!this.state.selectedRows.length) {
            Core.warningNotification("لطفا حداقل یک پرونده را انتخاب نمائیید");
            return;
        }
        let voucher: IDamageCaseVoucher = {
            RowGuid: Core.newId(),
            State: enmEntityState.Added,
            Items: [],
            ChequeNo: values["ChequeNo"],
            ChequeDate: values["ChequeDate"]
        } as any;
        this.state.selectedRows.forEach(row => {
            let damageCaseEvaluator = row.DamageCaseEvaluators[row.DamageCaseEvaluators.length - 1];
            let estimation = damageCaseEvaluator ? damageCaseEvaluator.Estimations[damageCaseEvaluator.Estimations.length - 1] : undefined;
            let policyholderConfirmation = damageCaseEvaluator ? damageCaseEvaluator.PolicyholderConfirmations[damageCaseEvaluator.PolicyholderConfirmations.length - 1] : undefined;

            voucher.Items.push({
                RowGuid: Core.newId(),
                DamageCaseId: row.Id,
                State: enmEntityState.Added,
                Amount: (estimation?.PayableAmount) ?? 0,
                PolicyholderFullName: (row.PolicyEquipmentModel.Policy.Policyholder?.FullName) ?? "",
                PolicyholderNationalCode: (row.PolicyEquipmentModel.Policy.Policyholder?.NationalCode) ?? "",
                ShebaNo: (policyholderConfirmation?.ShebaNo) ?? ""
            } as any);
        });

        let fetchOpt: ICallOptions = {
            url: "damageCaseVoucherPaymentDraft/issueVoucher",
            data: { voucher },
            onSuccess: (result: IResult) => {
                if (result.ValidationState.Successful) {
                    Core.successNotification("حواله باموفقیت صادر شد");
                    this.props.onClose && this.props.onClose(this);
                }
                //else
                //    Core.errorNotification(result.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.Error)?.Message);
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }
    onSelectRows = (selectedRows: IDamageCase[]) => {
        let sum = 0;
        selectedRows.forEach(record => {
            let damageCaseEvaluator = record.DamageCaseEvaluators[record.DamageCaseEvaluators.length - 1];
            let estimation = damageCaseEvaluator ? damageCaseEvaluator.Estimations[damageCaseEvaluator.Estimations.length - 1] : undefined;
            if (estimation?.PayableAmount)
                sum += estimation.PayableAmount;
        });
        this.setState({ selectedRows, totalAmount: sum });
    }
    virtualRender() {

        const header = this.getPageHeader({ title: "صدور حواله", onBack: () => { this.props.onClose && this.props.onClose(this) } });

        const columns: ColumnType<any>[] = [
            {
                title: "شماره پرونده",
                dataIndex: "Code"
            },
            {
                title: "کد ملی بیمه گزار/مشتری",
                dataIndex: "Policy.Policyholder.NationalCode",
                render: (_: any, record: IDamageCase) => {
                    return (
                        <>
                            {record.PolicyEquipmentModel.Policy.Policyholder?.NationalCode}
                        </>
                    );
                }
            },
            {
                title: "بیمه گزار/مشتری",
                dataIndex: "Policy.Policyholder.FullName",
                render: (_: any, record: IDamageCase) => {
                    return (
                        <>
                            {record.PolicyEquipmentModel.Policy.Policyholder?.FullName}
                        </>
                    );
                }
            },
            {
                title: "مبلغ قابل پرداخت",
                dataIndex: "PayableAmount",
                render: (_: any, record: IDamageCase) => {
                    let damageCaseEvaluator = record.DamageCaseEvaluators[record.DamageCaseEvaluators.length - 1];
                    let estimation = damageCaseEvaluator ? damageCaseEvaluator.Estimations[damageCaseEvaluator.Estimations.length - 1] : undefined;
                    return (
                        <>
                            {estimation ? `ريال ${estimation.PayableAmount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0}
                        </>
                    );
                }
            },
            {
                title: "شماره شبای بانکی",
                dataIndex: "ShebaNo",
                render: (_: any, record: IDamageCase) => {
                    let damageCaseEvaluator = record.DamageCaseEvaluators[record.DamageCaseEvaluators.length - 1];
                    let policyholderConfirmation = damageCaseEvaluator ? damageCaseEvaluator.PolicyholderConfirmations[damageCaseEvaluator.PolicyholderConfirmations.length - 1] : undefined;
                    return (
                        <>
                            {policyholderConfirmation?.ShebaNo}
                        </>
                    );
                }
            }
        ];
        const loading = this.state.loading ? { indicator: <CustomSpinner /> } : false;
        return (
            <>
                {header}
                <IssueVoucherForm
                    key="IssueVoucherForm"
                    loading={this.state.loading}
                    onClose={this.props.onClose}
                    onSubmit={this.onSubmit}
                    insurers={this.state.insurers}
                    totalAmount={this.state.totalAmount}
                />
                <div
                    key="datagrid"
                    className="box"
                    style={{ marginTop: 10 }}>

                    <Table
                        rowKey={r => r.RowGuid}
                        dataSource={this.state.data}
                        columns={columns}
                        loading={loading}
                        rowSelection={{
                            type: "checkbox",
                            onSelect: (_: IDamageCase, __: boolean, selectedRows: IDamageCase[]) => {
                                this.onSelectRows(selectedRows);
                            },
                            onSelectAll: (_: boolean, selectedRows: IDamageCase[]) => {
                                this.onSelectRows(selectedRows);
                            }
                        }}
                        pagination={
                            {
                                total: this.state.totalResultCount,
                                showTotal: (total, _) => {
                                    return (<div>تعداد کل ردیف ها: {total} </div>);
                                }
                            }
                        }
                    />
                </div>
            </>
        );
    }
}